import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import Badge from '../bookings/Badge';
import UsageBar from './UsageBar';
import type { UsageListResponse, UsageRow } from './types';
import {
  days_remaining,
  fmt_cycle_window,
  fmt_pct,
  messages_pct,
  minutes_pct,
  status_color,
  status_label,
  status_of,
} from './usage-logic';

function MetricRow({
  label,
  used,
  limit,
  percent,
  color,
}: {
  label: string;
  used: number;
  limit: number;
  percent: number;
  color: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>
          {/* round minutes for display; messages are already whole */}
          <strong style={{ color: 'var(--ink)' }}>{Math.round(used)}</strong> / {limit}{' '}
          ({fmt_pct(percent)})
        </span>
      </div>
      <UsageBar percent={percent} color={color} />
    </div>
  );
}

function PanelBody({ row }: { row: UsageRow }) {
  const status = status_of(row);
  const days = days_remaining(row.cycle.end);
  return (
    <div className="card" style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14 }}>
          Plan <strong style={{ textTransform: 'capitalize' }}>{row.plan_tier}</strong>
          {typeof row.monthly_price_usd === 'number' && (
            <span className="muted"> · ${row.monthly_price_usd}/mo</span>
          )}
        </span>
        <Badge color={status_color(status)}>{status_label(status)}</Badge>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
        <span className="muted">{fmt_cycle_window(row.cycle)}</span>
        <span>
          <strong>{days}</strong> day{days === 1 ? '' : 's'} left
        </span>
      </div>

      <MetricRow
        label="Minutes"
        used={row.used.minutes}
        limit={row.limits.minutes}
        percent={minutes_pct(row)}
        color={status_color(status)}
      />
      <MetricRow
        label="Messages"
        used={row.used.messages}
        limit={row.limits.messages}
        percent={messages_pct(row)}
        color={status_color(status)}
      />
    </div>
  );
}

/** Per-tenant usage panel (Prompt A.1), embedded on the tenant detail page. Pins one tenant
 *  via the proxy's tenant_id filter and renders the single returned row. */
export default function UsagePanel({ tenant_id }: { tenant_id: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['usage', tenant_id],
    queryFn: () =>
      api.get<UsageListResponse>(
        `/api/captomi-office/usage?tenant_id=${encodeURIComponent(tenant_id)}`,
      ),
  });

  if (isLoading) return <p className="muted">Loading usage…</p>;
  if (error) {
    return (
      <p className="error">
        Failed to load usage: {error instanceof ApiError ? error.reason : String(error)}
      </p>
    );
  }
  const row = data?.usage?.[0];
  if (!row) {
    return <p className="muted">No captomi_office usage for this tenant.</p>;
  }
  return <PanelBody row={row} />;
}

// Exported for unit tests that render the body with a fixture (no fetch).
export { PanelBody };
